import React, { useState, useEffect } from 'react'
import axios from 'axios';
import Select from 'react-select';
import { baseUrl } from "../../../comman/config";
import { can } from "../../../comman/permissions";
import { menuAboveTable } from "../../../comman/selectMenu";

// Audit Log. Entries cannot be edited and there is no Restore -- the client's sketch drew one and
// then asked for it to be left out. Putting one row back into a world that has moved on around it
// is how a restore quietly breaks the three screens downstream of it. This says what happened and
// who did it; undoing is done on the screen that owns the thing.
//
// The one write on the screen is Clear Log, which empties it. SuperAdmin only, and it records
// itself -- see the controller.
//
// Only SuperAdmin and Admin reach the screen at all -- the route says so, and the backend checks
// again, because hiding a screen is not a permission.

// The log can only be as long as the query allows, so the screen says when it has been cut off
// rather than quietly showing a partial answer as if it were the whole one.
const DEFAULT_LIMIT = 500;

// dd-mm-yyyy, as the sketch writes it, and a 12-hour clock with AM/PM.
const formatDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
        .format(date).replace(/\//g, '-');
};

const formatTime = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        .format(date);
};

function List() {

    const [rows, setRows] = useState([]);
    const [options, setOptions] = useState({ screens: [], users: [], operations: [] });
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [truncated, setTruncated] = useState(false);
    const [clearing, setClearing] = useState(false);
    // The entry being read in full. A value can run to a couple of thousand characters -- every
    // column of a row that changed twelve of them -- and a title tooltip shows that as one
    // unreadable line that vanishes when the pointer moves. Null is closed.
    const [detail, setDetail] = useState(null);

    // Emptying the log is the SuperAdmin's, not the Admin's: an Admin can read every entry and
    // clear none. The backend decides this again; hiding the button is presentation.
    const canClearLog = can('delete');

    // The sketch's filter row: Date, Time, Screen (multi), Operation (multi), Value, By (multi).
    // Date is a range rather than a single day -- a log is read as "what happened last week", and
    // one day at a time is the wrong tool for that. Time is left out for the same reason: a log
    // filtered to one minute of the day is a question nobody asks.
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [screenFilter, setScreenFilter] = useState([]);
    const [operationFilter, setOperationFilter] = useState([]);
    const [valueQuery, setValueQuery] = useState('');
    const [userFilter, setUserFilter] = useState([]);

    useEffect(() => {
        getOptions();
        getRows();
        // Loaded once on arrival; Submit re-runs it. The filters are applied by the server, so
        // typing in them does not re-query on every keystroke.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Escape closes the entry, which is what anybody who has ever met a dialog will try first.
    useEffect(() => {
        if (!detail) return undefined;
        const onKeyDown = (event) => { if (event.key === 'Escape') setDetail(null); };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [detail]);

    const getOptions = async () => {
        try {
            const response = await axios.get(`${baseUrl}/audit_log/filters`);
            setOptions(response.data?.data ?? { screens: [], users: [], operations: [] });
        } catch (error) {
            console.log(error);
        }
    };

    const getRows = async (overrides) => {
        const filters = {
            from: fromDate, to: toDate,
            screen_ids: screenFilter.map((s) => s.value).join(','),
            operations: operationFilter.map((o) => o.value).join(','),
            value: valueQuery.trim(),
            user_ids: userFilter.map((u) => u.value).join(','),
            ...overrides,
        };
        const query = Object.entries(filters)
            .filter(([, value]) => value !== '' && value !== undefined)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');

        setLoading(true);
        setLoadError('');
        try {
            const response = await axios.get(`${baseUrl}/audit_log/list${query ? `?${query}` : ''}`);
            const data = response.data?.data ?? [];
            setRows(data);
            setTruncated(data.length >= (response.data?.limit ?? DEFAULT_LIMIT));
        } catch (error) {
            console.log(error);
            setLoadError(error?.response?.data?.msg ?? 'Could not load the audit log.');
            setRows([]);
        }
        setLoading(false);
    };

    const handleClear = () => {
        setFromDate('');
        setToDate('');
        setScreenFilter([]);
        setOperationFilter([]);
        setValueQuery('');
        setUserFilter([]);
        getRows({ from: '', to: '', screen_ids: '', operations: '', value: '', user_ids: '' });
    };

    // Empties the log -- all of it, not the filtered view, which is what the button says and what
    // the backend does. Asked twice because there is no undo and no second copy: the confirmations
    // are the whole of the protection a user gets here.
    const handleClearLog = async () => {
        if (!window.confirm('Clear the audit log? This removes EVERY entry, not only the ones shown, and cannot be undone.')) return;
        if (!window.confirm('Last chance. The log will be empty apart from one entry recording this clearance. Proceed?')) return;

        setClearing(true);
        try {
            const response = await axios.delete(`${baseUrl}/audit_log/clear`);
            // The Screen and By dropdowns are built from what the log holds, so they are stale the
            // moment it is emptied -- refetched here rather than left offering entries that are
            // gone. The filters go back to blank for the same reason.
            setFromDate('');
            setToDate('');
            setScreenFilter([]);
            setOperationFilter([]);
            setValueQuery('');
            setUserFilter([]);
            await getOptions();
            await getRows({ from: '', to: '', screen_ids: '', operations: '', value: '', user_ids: '' });
            alert(`Cleared ${response.data?.cleared ?? 0} entries.`);
        } catch (error) {
            console.log(error);
            alert(error?.response?.data?.msg ?? 'Could not clear the audit log.');
        }
        setClearing(false);
    };

    const operationLabel = (operation) =>
        options.operations.find((option) => option.value === operation)?.label ?? operation;

    return (
        <div className="p-4">
            <div className="flex justify-between items-center py-4 px-6">
                <div>
                    <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">Audit Log</h1>
                </div>
                <div className="flex items-center gap-3">
                    {/* Red, and apart from the filter row, because it does not filter the view --
                        it empties the table. Only the SuperAdmin sees it. */}
                    {canClearLog && (
                        <button
                            onClick={handleClearLog}
                            disabled={clearing || loading}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring focus:ring-red-200 disabled:opacity-50"
                        >
                            {clearing ? 'Clearing...' : 'Clear Log'}
                        </button>
                    )}
                </div>
            </div>

            <div className="relative shadow-md sm:rounded-lg">
                <div className="pb-4 flex flex-wrap gap-3 items-center">
                    <div className="flex items-center gap-2">
                        <label htmlFor="from" className="text-sm text-gray-700">From</label>
                        <input
                            type="date" id="from" value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="block p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label htmlFor="to" className="text-sm text-gray-700">To</label>
                        <input
                            type="date" id="to" value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="block p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
                        />
                    </div>
                    <Select
                        {...menuAboveTable}
                        isMulti
                        classNamePrefix="select"
                        className="basic-multi-select min-w-56"
                        value={screenFilter}
                        onChange={(selected) => setScreenFilter(selected ?? [])}
                        options={options.screens}
                        placeholder="Screen"
                    />
                    <Select
                        {...menuAboveTable}
                        isMulti
                        classNamePrefix="select"
                        className="basic-multi-select min-w-56"
                        value={operationFilter}
                        onChange={(selected) => setOperationFilter(selected ?? [])}
                        options={options.operations}
                        placeholder="Operation"
                    />
                    <input
                        type="text"
                        value={valueQuery}
                        onChange={(e) => setValueQuery(e.target.value)}
                        placeholder="Value"
                        className="block p-2 text-sm text-gray-900 border border-gray-300 rounded-lg w-48 bg-gray-50"
                    />
                    <Select
                        {...menuAboveTable}
                        isMulti
                        classNamePrefix="select"
                        className="basic-multi-select min-w-48"
                        value={userFilter}
                        onChange={(selected) => setUserFilter(selected ?? [])}
                        options={options.users}
                        placeholder="By"
                    />
                    <button
                        onClick={() => getRows()}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200 disabled:opacity-50"
                    >
                        Submit
                    </button>
                    {/* "Reset", not "Clear". It empties the filters, and a button called Clear
                        sitting a few inches from one called Clear Log is how somebody wipes the
                        log meaning to blank a date field. */}
                    <button
                        onClick={handleClear}
                        disabled={loading}
                        className="px-4 py-2 bg-gray-200 text-gray-900 rounded-md hover:bg-gray-300 focus:outline-none focus:ring focus:ring-gray-200 disabled:opacity-50"
                    >
                        Reset
                    </button>
                </div>

                {loadError && <div className="text-red-500 pb-4">{loadError}</div>}
                {truncated && !loadError && (
                    <div className="text-amber-700 text-sm pb-4">
                        Showing the most recent {rows.length} entries. Narrow the dates to see older ones.
                    </div>
                )}

                {/* The header stays put while the rows scroll under it. */}
                <div className="overflow-auto max-h-[65vh]">
                    <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                            <tr>
                                <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Date</th>
                                <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Time</th>
                                <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Screen</th>
                                <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Operation</th>
                                <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Value</th>
                                <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((item) => (
                                <tr key={item.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                    <td className="px-6 py-3 whitespace-nowrap">{formatDate(item.created_at)}</td>
                                    <td className="px-6 py-3 whitespace-nowrap">{formatTime(item.created_at)}</td>
                                    <th scope="row" className="px-6 py-3 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                        {item.screen_name || '-'}
                                    </th>
                                    <td className="px-6 py-3 whitespace-nowrap">{operationLabel(item.operation)}</td>
                                    {/* Truncated in the row, because a value that wraps to five
                                        lines makes the list unreadable -- and clickable, because
                                        the truncated half is often the half you wanted. */}
                                    <td className="px-6 py-3 max-w-md">
                                        {item.value ? (
                                            <button
                                                type="button"
                                                onClick={() => setDetail(item)}
                                                className="block w-full text-left truncate text-blue-600 hover:underline focus:outline-none focus:ring focus:ring-blue-200 rounded"
                                            >
                                                {item.value}
                                            </button>
                                        ) : '-'}
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap">{item.user_name || '-'}</td>
                                </tr>
                            ))}
                            {rows.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-6 text-center text-gray-500">
                                        {loadError ? 'Nothing to show.' : 'No changes match these filters.'}
                                    </td>
                                </tr>
                            )}
                            {loading && (
                                <tr><td colSpan={6} className="px-6 py-6 text-center text-gray-500">Loading...</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* One entry, in full. Closes on Escape, on the backdrop, and on Close -- and stops the
                click inside the card from reaching the backdrop, which would shut it again. */}
            {detail && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
                    onClick={() => setDetail(null)}
                    role="presentation"
                >
                    <div
                        className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[85vh] flex flex-col"
                        onClick={(event) => event.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Audit log entry"
                    >
                        <div className="flex justify-between items-start px-6 py-4 border-b">
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">{detail.screen_name || 'Audit Log entry'}</h2>
                                <p className="text-sm text-gray-500">
                                    {operationLabel(detail.operation)} &middot; {formatDate(detail.created_at)} {formatTime(detail.created_at)} &middot; {detail.user_name || 'unknown'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setDetail(null)}
                                className="text-gray-400 hover:text-gray-700 text-2xl leading-none px-2"
                                aria-label="Close"
                            >
                                &times;
                            </button>
                        </div>

                        {/* The value verbatim. Not split on its commas into one change per line,
                            tempting as that is: a field value can contain a comma of its own
                            ("department: Masters, Reports"), and a popup whose whole job is to
                            show the full text must not quietly rewrite it. pre-wrap so it wraps
                            without being reflowed, and overflow so a long one scrolls inside the
                            card rather than pushing Close off the screen. */}
                        <div className="px-6 py-4 overflow-auto">
                            <pre className="whitespace-pre-wrap break-words font-sans text-sm text-gray-900">
                                {detail.value}
                            </pre>
                        </div>

                        {/* The request behind the entry, for tracing something the summary does
                            not explain. Small, because it is for whoever is debugging, not for
                            whoever is reading the log. */}
                        <div className="px-6 py-3 border-t text-xs text-gray-500 break-all">
                            {detail.method} {detail.path}
                        </div>

                        <div className="px-6 py-3 border-t flex justify-end">
                            <button
                                type="button"
                                onClick={() => setDetail(null)}
                                className="px-4 py-2 bg-gray-200 text-gray-900 rounded-md hover:bg-gray-300 focus:outline-none focus:ring focus:ring-gray-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default List;
